import type { Logger } from '@/modules/logger';
import type { RemoteQuizConfig } from '@/modules/quiz/onboardingSettings';
import type { ConfigBinding, ConfigMetadata, RemoteConfigProvider, ResolvedConfigs } from './types';
import { UrlConfigProvider } from './url';

export interface RemoteConfigOptions {
  search: URLSearchParams;
  logger: Logger;
  /** Lazy remote provider factories; called only when query has `config`/`amp_config`. */
  amplitude?: () => Promise<RemoteConfigProvider>;
  firebase?: () => Promise<RemoteConfigProvider>;
  /** Overall wait limit for remote sources; on expiry we use whatever arrived. */
  timeoutMs?: number;
}

export const DEFAULT_REMOTE_CONFIG_TIMEOUT_MS = 5000;

/** Remote config name: `amp_config` (Amplitude only) or `config` (both providers), as in WebQuiz. */
export const remoteConfigName = (search: URLSearchParams): string | null =>
  search.get('amp_config') || search.get('config') || null;

const withTimeout = async (task: Promise<void>, ms: number, onTimeout: () => void): Promise<void> => {
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = globalThis.setTimeout(() => {
      onTimeout();
      resolve();
    }, ms);
  });
  try {
    await Promise.race([task, timeout]);
  } finally {
    if (timer) globalThis.clearTimeout(timer);
  }
};

/** Port of WebQuiz `ConfigServiceFactory`: url always, remote only when named; provider errors are logged. */
export const resolveRemoteConfigs = async (options: RemoteConfigOptions): Promise<ResolvedConfigs> => {
  const url = new UrlConfigProvider(options.search);
  await url.init();
  const metadata: Partial<Record<ConfigBinding, ConfigMetadata>> = { [url.binding]: url.metadata };

  const result: ResolvedConfigs = { url: url.config, amplitude: null, firebase: null, metadata };
  if (!remoteConfigName(options.search)) return result;

  const remote: Array<
    [keyof Pick<ResolvedConfigs, 'amplitude' | 'firebase'>, (() => Promise<RemoteConfigProvider>) | undefined]
  > = [
    ['amplitude', options.amplitude],
    ['firebase', options.firebase],
  ];

  // Config is already applied after the timeout — late responses must not change it.
  let timedOut = false;
  const tasks = remote.map(async ([key, factory]) => {
    if (!factory) return;
    try {
      const provider = await factory();
      await provider.init();
      if (timedOut) {
        options.logger.logWarning({ provider: key }, 'remoteConfigIgnoredAfterTimeout');
        return;
      }
      metadata[provider.binding] = provider.metadata;
      result[key] = provider.config as RemoteQuizConfig | null;
    } catch (error) {
      options.logger.logError(error, `${key}ConfigProviderFailed`);
    }
  });

  await withTimeout(
    Promise.all(tasks).then(() => undefined),
    options.timeoutMs ?? DEFAULT_REMOTE_CONFIG_TIMEOUT_MS,
    () => {
      timedOut = true;
      options.logger.logWarning(
        { timeoutMs: options.timeoutMs ?? DEFAULT_REMOTE_CONFIG_TIMEOUT_MS },
        'remoteConfigTimeout',
      );
    },
  );
  options.logger.logInfo({ metadata }, 'configServiceInitialized');
  return result;
};
